import type { RailLayout } from "@/data/rail-branches";
import { createFrameMemo } from "../../rail/FrameMemo/memo";
import { branchAt } from "../../rail/RailBranchMotion/motion";
import { placeGroupsAt } from "../../rail/RailPlaceGroups/groups";
import type { CharacterMarkerGroup } from "../../board/ChapterBoard/types";

function activeRootPlaceIds(
  stays: RailLayout["stays"],
  playheadMs: number,
): string[] {
  const roots = new Set<string>();
  for (const stay of stays) {
    if (playheadMs >= stay.startMs && playheadMs < stay.endMs) {
      roots.add(stay.rootPlaceId);
    }
  }
  return [...roots].sort((a, b) => a.localeCompare(b));
}

/** Cohorts sharing a part at this moment (matches rail seating / place groups). */
export function characterGroupsAtPlayhead(
  rail: RailLayout,
  playheadMs: number,
): CharacterMarkerGroup[] {
  return cohortsMemo(rail, playheadMs, "", () => computeCohorts(rail, playheadMs));
}

const cohortsMemo = createFrameMemo<RailLayout, CharacterMarkerGroup[]>();

function computeCohorts(
  rail: RailLayout,
  playheadMs: number,
): CharacterMarkerGroup[] {
  const groups: CharacterMarkerGroup[] = [];
  for (const rootPlaceId of activeRootPlaceIds(rail.stays, playheadMs)) {
    const branch = branchAt(rail.branches, rootPlaceId, playheadMs);
    const branchId = branch?.id ?? rootPlaceId;
    for (const placeGroup of placeGroupsAt(rail.stays, rootPlaceId, playheadMs)) {
      groups.push({
        id: `${branchId}:${placeGroup.placeId}`,
        branchId,
        placeId: placeGroup.placeId,
        placeLabel: placeGroup.placeLabel ?? placeGroup.placeId,
        markerIds: placeGroup.characterIds,
      });
    }
  }
  return groups;
}

export function characterGroupIdByCharacter(
  groups: readonly CharacterMarkerGroup[],
): Map<string, string> {
  const byCharacter = new Map<string, string>();
  for (const group of groups) {
    for (const characterId of group.markerIds) {
      byCharacter.set(characterId, group.id);
    }
  }
  return byCharacter;
}
